/**
 * The alerts module's paths.
 *
 * `/alerts` is the centre — the feed everybody lands on. `/alerts/rules` is the
 * configuration surface and `/alerts/preferences` is per-user. The three are one
 * mental model and one nav entry (`nav:items.alerts`, already declared in
 * `layout/nav-items.ts`), so they share a prefix rather than each claiming a
 * top-level route.
 *
 * A selected alert is a QUERY PARAMETER, not a path segment. The detail is a drawer
 * over the feed, and a manager who pastes the URL into a ticket should hand over the
 * filtered list AND the alert they were looking at — which a `/alerts/:id` route
 * would lose. It also means dismissing the drawer is a history entry rather than a
 * navigation back to an unfiltered feed.
 *
 * Route paths stay in English while the UI defaults to French: a localized URL set
 * means two routing tables and a redirect on every language switch, and the address
 * bar is not a translated surface.
 *
 * Registration lives in `routes/app-routes.tsx`, which this phase does not own —
 * NOTES-PHASE7-WEB.md §1 carries the exact patch.
 */

export const ALERTS_PATH = '/alerts'
export const ALERT_RULES_PATH = '/alerts/rules'
export const ALERT_RULE_NEW_PATH = '/alerts/rules/new'
export const ALERT_RULE_EDIT_PATH = '/alerts/rules/:ruleId'
export const ALERT_PREFERENCES_PATH = '/alerts/preferences'

export const ALERT_QUERY_KEYS = {
  /** The alert whose detail drawer is open. */
  alert: 'alert',
  eventType: 'type',
  severity: 'severity',
  status: 'status',
  source: 'source',
  vehicleId: 'vehicle',
  from: 'from',
  to: 'to',
  /** `1` narrows to what still needs attention — the centre's default landing state. */
  unresolved: 'unresolved',
} as const

export interface AlertFeedParams {
  eventType?: string
  severity?: string
  status?: string
  source?: string
  vehicleId?: string
  /** ISO instants or `YYYY-MM-DD` day keys; the feed converts before calling. */
  from?: string
  to?: string
  unresolvedOnly?: boolean
  alertId?: string
}

function searchFrom(params: AlertFeedParams): string {
  const search = new URLSearchParams()
  const set = (key: string, value: string | undefined) => {
    if (value !== undefined && value !== '') search.set(key, value)
  }
  set(ALERT_QUERY_KEYS.eventType, params.eventType)
  set(ALERT_QUERY_KEYS.severity, params.severity)
  set(ALERT_QUERY_KEYS.status, params.status)
  set(ALERT_QUERY_KEYS.source, params.source)
  set(ALERT_QUERY_KEYS.vehicleId, params.vehicleId)
  set(ALERT_QUERY_KEYS.from, params.from)
  set(ALERT_QUERY_KEYS.to, params.to)
  if (params.unresolvedOnly === true) search.set(ALERT_QUERY_KEYS.unresolved, '1')
  set(ALERT_QUERY_KEYS.alert, params.alertId)

  const query = search.toString()
  return query.length > 0 ? `?${query}` : ''
}

export function alertCentrePath(params: AlertFeedParams = {}): string {
  return `${ALERTS_PATH}${searchFrom(params)}`
}

export function alertRulePath(ruleId: string): string {
  return `${ALERT_RULES_PATH}/${ruleId}`
}
